/**
 * agentThemes — shared theme registry for agent surfaces (2026-07-04).
 *
 * The 4 GHL visual themes (originally in components/modules/ghl/theme.ts) are
 * promoted here so they can skin MULTIPLE agent surfaces — the Pi chat dock and
 * the embedded hermes dashboard — not just the GHL CRM viewer. Operator
 * 2026-07-04: "use those same themes and be able to apply them to hermes as well
 * as pi chat agents … set per chat tab."
 *
 * `components/modules/ghl/theme.ts` now re-exports from here, so the GHL module +
 * its tests are unchanged (one source of truth). The CRM-specific knobs
 * (accentEdge, showWinMeter, layout, chip, …) stay on the type — the GHL viewer
 * still uses them — while the chat/hermes consumers use only the color subset
 * via `agentThemeVars()`.
 *
 * Colors are applied via INLINE styles (hue palette + alpha()), never dynamic
 * Tailwind class names, so JIT purge can never strip them.
 */

export type GhlThemeId = 'vivid' | 'aurora' | 'slate' | 'minimal';

export interface GhlTheme {
  id: GhlThemeId;
  label: string;
  /** Ordered hue palette: cycled per pipeline stage, hashed per person card. */
  hues: string[];
  /** People / calendar / conversation card background (hex or 'transparent'). */
  cardBg: string;
  /** Nested card (opportunity) background (hex or 'transparent'). */
  cardRaisedBg: string;
  /** Where the accent hue paints a people-card edge. */
  accentEdge: 'left' | 'none';
  /** Tint the pipeline stage header with the stage hue. */
  tintStageHeader: boolean;
  /** Show the win-probability meter under a stage header. */
  showWinMeter: boolean;
  /** Show initial-avatars on people cards. */
  showAvatars: boolean;
  /** List vs grid layout for contacts / conversations / calendars. */
  layout: 'grid' | 'list';
  /** Chip treatment for hued chips (type / status / calendarType). */
  chip: 'tinted' | 'outline' | 'solid';
  /** Monetary-value accent color. */
  money: string;
  /** Tint strength 0..1 (scales header / avatar / chip alpha). */
  intensity: number;
}

export const GHL_THEMES: Record<GhlThemeId, GhlTheme> = {
  vivid: {
    id: 'vivid',
    label: 'Vivid',
    hues: ['#6366f1', '#a855f7', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#0ea5e9', '#8b5cf6'],
    cardBg: '#17171b',
    cardRaisedBg: '#1e1e23',
    accentEdge: 'left',
    tintStageHeader: true,
    showWinMeter: true,
    showAvatars: true,
    layout: 'grid',
    chip: 'tinted',
    money: '#22c55e',
    intensity: 1,
  },
  aurora: {
    id: 'aurora',
    label: 'Aurora',
    hues: ['#818cf8', '#e879f9', '#22d3ee', '#34d399', '#fbbf24', '#fb7185', '#38bdf8', '#a78bfa'],
    cardBg: '#14141a',
    cardRaisedBg: '#1c1c26',
    accentEdge: 'left',
    tintStageHeader: true,
    showWinMeter: true,
    showAvatars: true,
    layout: 'grid',
    chip: 'solid',
    money: '#34d399',
    intensity: 1,
  },
  slate: {
    id: 'slate',
    label: 'Slate',
    hues: ['#60a5fa', '#818cf8', '#2dd4bf', '#4ade80', '#a3a3a3', '#94a3b8', '#38bdf8', '#c084fc'],
    cardBg: '#1a1b1f',
    cardRaisedBg: '#212229',
    accentEdge: 'left',
    tintStageHeader: false,
    showWinMeter: true,
    showAvatars: true,
    layout: 'grid',
    chip: 'outline',
    money: '#22c55e',
    intensity: 0.5,
  },
  minimal: {
    id: 'minimal',
    label: 'Minimal',
    hues: ['#a1a1aa', '#818cf8', '#a1a1aa', '#a1a1aa', '#a1a1aa', '#a1a1aa', '#a1a1aa', '#a1a1aa'],
    cardBg: 'transparent',
    cardRaisedBg: 'transparent',
    accentEdge: 'none',
    tintStageHeader: false,
    showWinMeter: false,
    showAvatars: false,
    layout: 'list',
    chip: 'outline',
    money: '#22c55e',
    intensity: 0.35,
  },
};

export const DEFAULT_GHL_THEME_ID: GhlThemeId = 'vivid';

/** Append a 0..1 alpha as a 2-hex-digit suffix to a #rrggbb hue. */
export function alpha(hex: string, a: number): string {
  const clamped = Math.max(0, Math.min(1, a));
  return `${hex}${Math.round(clamped * 255)
    .toString(16)
    .padStart(2, '0')}`;
}

/**
 * Map a theme's palette onto the shadcn/Tailwind CSS custom properties that
 * @earendil-works/pi-web-ui consumes (verified: it uses `var(--token)` DIRECTLY,
 * not `hsl(var(--token))`, so any CSS color — incl. hex — is valid). Set the
 * result as INLINE style on the chat surface so it overrides pi-web-ui's own
 * `:host` token defaults regardless of shadow/light DOM. Minimal's transparent
 * backgrounds fall back to concrete dark values so the chat stays legible.
 */
export function agentThemeVars(theme: GhlTheme): Record<string, string> {
  const bg = theme.cardBg === 'transparent' ? '#16161a' : theme.cardBg;
  const raised = theme.cardRaisedBg === 'transparent' ? '#1e1e23' : theme.cardRaisedBg;
  const accent = theme.hues[0];
  const accent2 = theme.hues[1] ?? accent;
  return {
    // shadcn tokens pi-web-ui renders against
    '--background': bg,
    '--card': raised,
    '--popover': raised,
    '--secondary': raised,
    '--muted': raised,
    '--sidebar': bg,
    '--accent': accent,
    '--primary': accent,
    '--ring': accent,
    '--chart-1': accent,
    '--chart-2': accent2,
    '--border': alpha(accent, 0.28),
    '--input': alpha(accent, 0.28),
    '--foreground': '#e7e7ea',
    '--card-foreground': '#e7e7ea',
    '--popover-foreground': '#e7e7ea',
    '--secondary-foreground': '#e7e7ea',
    '--accent-foreground': '#ffffff',
    '--primary-foreground': '#ffffff',
    '--muted-foreground': '#a1a1aa',
    // vibe-side aliases so the dock chrome can match too. These drive the scoped
    // `.pi-agent-themed .bg-*/.border-*` overrides (PiChatDock.agentTheme.css)
    // that recolor pi-web-ui's baked-literal Tailwind utilities.
    '--agent-accent': accent,
    '--agent-bg': bg,
    '--agent-raised': raised,
    '--agent-border': alpha(accent, 0.28),
  };
}

/**
 * Lighten a #rrggbb toward white by `amount` (0..1 = fraction of white mixed
 * in). Used to turn a GHL accent hue into a readable light "ink" color for
 * hermes's midground layer (its primary text + accent color).
 */
export function lighten(hex: string, amount: number): string {
  const a = Math.max(0, Math.min(1, amount));
  const ch = (i: number) => parseInt(hex.slice(i, i + 2), 16);
  const mix = (c: number) => Math.round(c * (1 - a) + 255 * a);
  const [r, g, b] = [ch(1), ch(3), ch(5)];
  return `#${[mix(r), mix(g), mix(b)]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * The `:root` stylesheet that recolors the embedded hermes dashboard to a GHL
 * theme (live-verified 2026-07-04). Hermes derives EVERY `--color-*` token via
 * `color-mix()` from three base layers (background / midground / foreground) —
 * see hermes web/src/index.css + themes/context.tsx `layerVars`. Its runtime
 * ThemeProvider writes those bases as INLINE style on the iframe's `:root`, so
 * a `<style>` block with `!important` wins AND survives hermes re-applying its
 * own theme (which only touches inline styles). We override just the two
 * VISIBLE layers — background (canvas) and midground (text + derived accent);
 * foreground is an invisible highlight (alpha 0) in every hermes theme. All
 * derived tokens (`--color-card/-primary/-accent/-border/…`) recompute from our
 * bases automatically. midground = the GHL accent lightened so it reads as text
 * while still carrying the theme's hue.
 */
export function hermesThemeCss(theme: GhlTheme): string {
  const bg = theme.cardBg === 'transparent' ? '#16161a' : theme.cardBg;
  const mid = lighten(theme.hues[0], 0.55);
  return `:root{
  --background: color-mix(in srgb, ${bg} 100%, transparent) !important;
  --background-base: ${bg} !important;
  --background-alpha: 1 !important;
  --midground: color-mix(in srgb, ${mid} 100%, transparent) !important;
  --midground-base: ${mid} !important;
  --midground-alpha: 1 !important;
}`;
}

const readThemeId = (key: string): GhlThemeId => {
  try {
    const v = localStorage.getItem(key);
    if (v && v in GHL_THEMES) return v as GhlThemeId;
  } catch {
    /* localStorage unavailable — fall through to the default */
  }
  return DEFAULT_GHL_THEME_ID;
};

const writeThemeId = (key: string, id: GhlThemeId): void => {
  try {
    localStorage.setItem(key, id);
  } catch {
    /* best-effort persistence — ignore quota / disabled-storage errors */
  }
};

const themeKey = (tenantId: string | null): string => `ghl_theme_${tenantId || 'default'}`;
const hermesThemeKey = (tenantId: string | null): string =>
  `hermes_theme_${tenantId || 'default'}`;

/** Load the persisted per-tenant Pi/GHL theme id (defaults to Vivid). */
export function loadGhlThemeId(tenantId: string | null): GhlThemeId {
  return readThemeId(themeKey(tenantId));
}

/** Persist the per-tenant Pi/GHL theme id (best-effort). */
export function saveGhlThemeId(tenantId: string | null, id: GhlThemeId): void {
  writeThemeId(themeKey(tenantId), id);
}

/**
 * Load the persisted per-tenant HERMES theme id (own key — the hermes GUI is a
 * single surface, themed independently from the per-tab Pi chat). Defaults to
 * Vivid.
 */
export function loadHermesThemeId(tenantId: string | null): GhlThemeId {
  return readThemeId(hermesThemeKey(tenantId));
}

/** Persist the per-tenant hermes theme id (best-effort). */
export function saveHermesThemeId(tenantId: string | null, id: GhlThemeId): void {
  writeThemeId(hermesThemeKey(tenantId), id);
}
